import { Mail, MapPin, Phone } from 'lucide-react';
import type { AccountAddress, AccountContact } from '@/api/commercial/accounts';
import {
  ResumeCard,
  ResumeCardHeader,
} from '@/components/commercial/proposals/shared/summary-components';

interface ResumeContactAddressSectionProps {
  contacts: AccountContact[];
  deliveryAddress: AccountAddress | null;
  deliveryMode?: 'CLIENT' | 'BWS';
  pickupLocationName?: string | null;
}

function formatAddressParts(address: AccountAddress) {
  const parts: string[] = [];
  if (address.street && address.number && address.district) {
    parts.push(`${address.street}, ${address.number}, ${address.district}`);
  }
  if (address.city && address.state && address.zip_code) {
    parts.push(`${address.city} - ${address.state}, ${address.zip_code}`);
  }
  if (address.complement) {
    parts.push(`Complemento: ${address.complement}`);
  }
  return parts;
}

export function ResumeContactAddressSection({
  contacts,
  deliveryAddress,
  deliveryMode,
  pickupLocationName,
}: ResumeContactAddressSectionProps) {
  return (
    <ResumeCard>
      <ResumeCardHeader title="Contato e entrega" />

      <div className="grid gap-0 md:grid-cols-2">
        <div className="min-w-0 border-b border-gray-100 px-5 py-4 md:border-b-0 md:border-r">
          <div className="mb-2.5 flex items-center gap-1.5">
            <Phone className="h-3.5 w-3.5 text-blue-500" />
            <p className="text-xs font-bold text-gray-700">Contato</p>
          </div>
          {contacts.length > 0 ? (
            <div className="space-y-2.5">
              {contacts.map((contact) => (
                <div key={contact.id} className="min-w-0 text-xs leading-4">
                  <p className="truncate font-bold text-gray-900">{contact.name}</p>
                  {contact.positionOrRelation && (
                    <p className="truncate font-semibold text-gray-600">
                      {contact.positionOrRelation}
                    </p>
                  )}
                  {contact.contactItems.map((item) => (
                    <p key={item.id} className="flex min-w-0 items-center gap-1.5 text-gray-600">
                      {item.type === 'Email' ? (
                        <Mail className="h-3 w-3 shrink-0 text-gray-400" />
                      ) : (
                        <Phone className="h-3 w-3 shrink-0 text-gray-400" />
                      )}
                      <span className="truncate">{item.contact}</span>
                    </p>
                  ))}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs text-muted-foreground">-</p>
          )}
        </div>

        <div className="min-w-0 px-5 py-4">
          <div className="mb-2.5 flex items-center gap-1.5">
            <MapPin className="h-3.5 w-3.5 text-green-500" />
            <p className="text-xs font-bold text-gray-700">
              {deliveryMode === 'BWS' ? `Retirar na BWS — ${pickupLocationName ?? ''}` : 'Entrega'}
            </p>
          </div>
          {deliveryAddress ? (
            <div className="space-y-0.5 text-xs leading-4 text-gray-700">
              {formatAddressParts(deliveryAddress).map((part) => (
                <p key={part} className="truncate">
                  {part}
                </p>
              ))}
            </div>
          ) : (
            <p className="text-xs text-muted-foreground">-</p>
          )}
        </div>
      </div>
    </ResumeCard>
  );
}
